import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    AddressTypesEnum,
    IndividualAddressesService,
    IndividualAddressInterface,
    CountryModel,
    IndividualAddressModel,
    IndividualAddressInputModel,
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {BaseControl, CountryAutocompleteControl, Helper} from '../../../../shared/classes';

import {AddressesFormErrorStatesEnum} from '../../enums/error-states';

import {
    AddressControls,
    AddressSelector,
    AddressIdSelector,
    AddressCitySelector,
    AddressTypeSelector,
    AddressStateSelector,
    AddressCountrySelector,
    AddressesFormArraySelector,
    AddressPostalCodeSelector,
} from '../../../../../assets/descriptors/panel-clients';

export class AddressesForm {
    private readonly individualId: number;
    private readonly countries: CountryModel[];
    private readonly addressesService: IndividualAddressesService;

    private addresses: IndividualAddressModel[];

    public readonly addressesFormArraySelector: string;

    public form: FormGroup;
    public controls: Array<BaseControl[]>;
    public controlsObservable: BehaviorSubject<Array<BaseControl[]>>;

    constructor(
        mainAddressType: string,
        individualId: number,
        addresses: IndividualAddressModel[],
        countries: CountryModel[],
        addressesService: IndividualAddressesService
    ) {
        this.individualId = individualId;
        this.addresses = !addresses ? [] : addresses;
        this.countries = !countries ? [] : countries;
        this.addressesService = addressesService;

        this.addressesFormArraySelector = AddressesFormArraySelector;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private initControls(model?: IndividualAddressModel, type?: string): BaseControl[] {
        const controls = AddressControls();

        return controls.map((control: BaseControl) => {
            if (model) {
                control.value = this.getAddressProperty(control.name, model);
            } else {
                if (control.name === AddressTypeSelector) {
                    control.value = type;
                }
            }

            const placeholder = control.placeholder.toLowerCase();

            control.placeholder =
                (type && (type === AddressTypesEnum.Mailing)) ? 'Mailing ' + placeholder : 'Residential ' + placeholder;

            if (control.name === AddressCountrySelector) {
                const countriesIds = this.countries.map((country: CountryModel) => country.id);

                control.validators.push(InArrayValidator(countriesIds));

                (control as CountryAutocompleteControl).countries = this.countries;
            }

            return control;
        });
    }

    private getAddressProperty(selector: string, model: IndividualAddressModel): string | number {
        switch (selector) {
            case AddressIdSelector:
                return model.id;
            case AddressCountrySelector:
                return model.countryId;
            case AddressCitySelector:
                return model.city;
            case AddressStateSelector:
                return model.stateProvinceCounty;
            case AddressPostalCodeSelector:
                return model.zipCode;
            case AddressSelector:
                return model.addressLine;
            case AddressTypeSelector:
                return model.addressType;
            default:
                return null;
        }
    }

    private removeEmptyGroups(emptyGroupIndexes: number[], formArray: FormArray): void {
        emptyGroupIndexes.sort((previousIndex: number, nextIndex: number) => {
            return nextIndex - previousIndex;
        }).map((index: number) => {
            formArray.removeAt(index);

            this.controls.splice(index, 1);
        });
    }

    private createUpdateRequests(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === AddressIdSelector) &&
                        formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .filter(this.removeUnchangedAddresses.bind(this))
            .map(this.createUpdateRequest.bind(this));
    }

    private removeUnchangedAddresses(formGroup: FormGroup): boolean {
        const filteredAddresses = this.addresses.filter((address: IndividualAddressModel) => {
            return address.id === formGroup.controls[AddressIdSelector].value;
        });

        if (filteredAddresses.length === 0) {
            return false;
        }

        const valuesCheck = Object.keys(formGroup.controls).map((formControlKey: string) => {
            const formControlValue = formGroup.controls[formControlKey].value;

            return this.checkAddressDetailProperty(
                formControlKey,
                formControlValue,
                filteredAddresses[0]
            );
        });

        return valuesCheck.indexOf(false) >= 0;
    }

    private checkAddressDetailProperty(
        selector: string,
        formPropertyValue: number | string,
        model: IndividualAddressModel
    ): boolean {
        switch (selector) {
            case AddressIdSelector:
                return model.id === formPropertyValue;
            case AddressCountrySelector:
                return model.countryId === formPropertyValue;
            case AddressCitySelector:
                return model.city === formPropertyValue;
            case AddressStateSelector:
                return model.stateProvinceCounty === formPropertyValue;
            case AddressPostalCodeSelector:
                return model.zipCode === formPropertyValue;
            case AddressSelector:
                return model.addressLine === formPropertyValue;
            case AddressTypeSelector:
                return model.addressType === formPropertyValue;
            default:
                return false;
        }
    }

    private createUpdateRequest(formGroup: FormGroup): Observable<ApiResponse<ApiError[] | boolean>> {
        const filteredAddresses = this.addresses.filter((address: IndividualAddressModel) => {
            return address.id === formGroup.controls[AddressIdSelector].value;
        });

        const clonedModel = filteredAddresses[0].serialize();

        filteredAddresses[0].countryId =
            formGroup.controls[AddressCountrySelector].value;

        filteredAddresses[0].city =
            formGroup.controls[AddressCitySelector].value;

        filteredAddresses[0].stateProvinceCounty =
            formGroup.controls[AddressStateSelector].value;

        filteredAddresses[0].zipCode =
            formGroup.controls[AddressPostalCodeSelector].value;

        filteredAddresses[0].addressLine =
            formGroup.controls[AddressSelector].value;

        return this.addressesService.update(filteredAddresses[0])
            .pipe(
                map(this.updateRequestMap.bind(this, clonedModel))
            );
    }

    private updateRequestMap(
        clonedModel: IndividualAddressInterface,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const model = new IndividualAddressModel(clonedModel);

            this.addresses = this.addresses
                .map((address: IndividualAddressModel) => {
                    if (address.id === model.id) {
                        address = model;
                    }

                    return address;
                });

            return Helper.errorsHelper().createErrorResponse<null>(
                response,
                AddressesFormErrorStatesEnum.UpdateAddressFailed
            );
        }

        return Helper.createSuccessResponse<boolean>(true);
    }

    private createRegistrationRequests(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === AddressIdSelector) &&
                        !formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .map(this.createRegistrationRequest.bind(this));
    }

    private createRegistrationRequest(formGroup: FormGroup): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = new IndividualAddressInputModel(
            this.individualId,
            formGroup.value[AddressTypeSelector],
            formGroup.value[AddressCountrySelector],
            formGroup.value[AddressCitySelector],
            formGroup.value[AddressStateSelector],
            formGroup.value[AddressPostalCodeSelector],
            formGroup.value[AddressSelector]
        );

        return this.addressesService.register(inputModel)
            .pipe(
                map(this.registrationRequestMap.bind(this))
            );
    }

    private registrationRequestMap(
        response: ApiResponse<ApiError[] | IndividualAddressInterface>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<IndividualAddressInterface>(
                response,
                AddressesFormErrorStatesEnum.CreateAddressFailed
            );
        }

        const data = response.data as IndividualAddressInterface;
        const model = new IndividualAddressModel(data);

        if (!this.addresses) {
            this.addresses = [];
        }

        this.addresses.push(model);

        return Helper.createSuccessResponse<boolean>(true);
    }

    addFormGroup(type: string): void {
        const addressTypes = [AddressTypesEnum.Residential, AddressTypesEnum.Mailing] as string[];

        if (addressTypes.indexOf(type) > 0) {
            const formGroup = new FormGroup({});
            const formArray = this.form.controls[this.addressesFormArraySelector] as FormArray;

            const controls = this.initControls(null, type).map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));

                return control;
            });

            formArray.push(formGroup);

            this.controls.push(controls);
            this.controlsObservable.next(this.controls);
        }
    }

    initForm(type: string = AddressTypesEnum.Residential): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (this.addresses && this.addresses.length !== 0) {
            this.controls = this.addresses.map((address: IndividualAddressModel) => {
                return this.initControls(address, type);
            });
        } else {
            const controls = this.initControls(null, type);

            this.controls.push(controls);
        }

        const formArray = new FormArray([]);

        this.controls.map((controls: BaseControl[]) => {
            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            formArray.push(formGroup);
        });

        this.form.addControl(this.addressesFormArraySelector, formArray);
        this.controlsObservable.next(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const emptyFormGroupIndexes = [];
        const formArray = (this.form.controls[this.addressesFormArraySelector] as FormArray);

        const statuses = formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
            const emptyGroupCheck = Object.keys(formGroup.controls)
                .filter((controlKey: string) => [AddressIdSelector, AddressTypeSelector].indexOf(controlKey) < 0)
                .map((controlKey: string) => !formGroup.controls[controlKey].value);

            if ((emptyGroupCheck.indexOf(false) < 0) && (formGroupIndex > 0)) {
                emptyFormGroupIndexes.push(formGroupIndex);

                return true;
            }

            if (formGroup.invalid) {
                Object.keys(formGroup.controls).map((controlKey: string, controlIndex: number) => {
                    const formControl = formGroup.controls[controlKey];
                    const control = this.controls[formGroupIndex][controlIndex];

                    if (formControl.invalid) {
                        const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                            return messageFunction(control.placeholder)[errorKey];
                        });

                        control.setErrors(errors);
                    }
                });

                return false;
            }

            return true;
        });

        if (emptyFormGroupIndexes.length !== 0) {
            this.removeEmptyGroups(emptyFormGroupIndexes, formArray);
        }

        return statuses.indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (this.form.dirty) {
            const requests = [];

            const formArray = this.form.controls[this.addressesFormArraySelector] as FormArray;

            if (this.addresses && this.addresses.length !== 0) {
                const updateRequests = this.createUpdateRequests(formArray);

                if (updateRequests.length !== 0) {
                    requests.push(...updateRequests);
                }
            }

            const registrationRequests = this.createRegistrationRequests(formArray);

            if (registrationRequests.length !== 0) {
                requests.push(...registrationRequests);
            }

            if (requests.length !== 0) {
                return forkJoin(requests)
                    .pipe(
                        map((responses: ApiResponse<ApiError[] | boolean>[]) => {
                            if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                                return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                            }

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
            }
        }

        return null;
    }
}
